import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { applyMiddleware, createStore, compose } from 'redux';
import { apiMiddleware } from 'redux-api-middleware';
import { makeInitialState } from './core/redux/appState';
import { loggingMiddleware } from './core/redux/middleware/loggingMiddleware';
import { noOpMiddleware } from './core/redux/middleware/noOpMiddleware';
import { rootReducer } from './core/redux/rootReducer';
import { IS_DEV_ENV, IS_TEST_ENV } from './core/windowProps';
import './index.css';
import AppStateSessionStorageBridge from './localStorage/AppStateSessionStorageBridge';
import * as serviceWorker from './serviceWorker';
import TranslationLoader from './localization/TranslationLoader';
import { makeRoutes } from './navigation/routes';
import ConfigLoader from './featureFlags/ConfigLoader';
import RtlToggle from './localization/RtlToggle';
import { Global } from '@emotion/core';
import { GLOBAL_FONTS } from './globalStyle/fonts';
import LocalDiv from './localization/LocalDiv';
import AppVersion from './version/AppVersion';
import OldRouteInterceptor from './navigation/OldRouteInterceptor';
import { defaultLanguages } from './localization/defaultLanguages';
import { AnimatePresence } from 'framer-motion';
import { isOnMobile } from './language/browser';
import './globalStyle/date-picker-cal.css';
import LogoutTimer from './auth/LogoutTimer';
import TimeoutModal from './TimeoutModal';
import AppRouter from './navigation/AppRouter';


const devTools      = (window as any).__REDUX_DEVTOOLS_EXTENSION__;
const devMiddleware = IS_DEV_ENV && devTools ? devTools() : (f: any) => f;
const logger        = IS_DEV_ENV || IS_TEST_ENV ? loggingMiddleware : noOpMiddleware;

// Set the default locale for now. We'll try to set it for real when the TranslationLoader mounts.
const initialAppState = makeInitialState(defaultLanguages['en'], isOnMobile(window));

const enhancers = compose(
  applyMiddleware(
    logger, // Keep first to log all incoming actions unaltered by subsequent middleware
    apiMiddleware,
  ),
  devMiddleware,
);

export const store = createStore(rootReducer, initialAppState, enhancers);


const routes = makeRoutes();


ReactDOM.render(
  <React.StrictMode>
    {/* Global Styles */}
    <Global styles={GLOBAL_FONTS} />

    <Provider store={store}>
      <BrowserRouter basename={routes.Root}>
        {/* Non-Rendering Components */}
        <AppStateSessionStorageBridge />
        <TranslationLoader />
        <ConfigLoader />
        <LogoutTimer />

        <LocalDiv id='root'>
          <AnimatePresence>
            <AppRouter />
          </AnimatePresence>

          <OldRouteInterceptor />

          <RtlToggle />
          <AppVersion />
          <TimeoutModal />

        </LocalDiv>

      </BrowserRouter>
    </Provider>
  </React.StrictMode>,
  document.getElementById('react-root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
